import { useState, type ReactNode } from 'react';
import { Shield, AlertTriangle, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SeasonEditUnlockBanner } from '@/components/SeasonEditUnlockBanner';
import {
  AccessStateBanner,
  type AccessStateInfo,
} from '@/components/AccessStateBanner';
import { SetupIncompleteBanner } from '@/components/SetupIncompleteBanner';

/** Локальный dismiss 2FA-баннера на 24ч.
 *  Founder feedback 2026-05-02: «нужен крестик который закроет бар». */
const TWO_FA_DISMISS_KEY = 'ikolhoz.2fa_reminder_dismissed_until';
const TWO_FA_DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function read2FADismissedUntil(): number {
  try {
    const raw = localStorage.getItem(TWO_FA_DISMISS_KEY);
    if (!raw) return 0;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

export interface StackedBannersProps {
  impersonation?: {
    is_impersonating: boolean;
    tenant_name?: string | null;
    /**
     * ФИО пользователя, под которым работаем (AK-470). Пусто — это вход
     * в компанию целиком (AK-13), там конкретного человека нет.
     */
    user_full_name?: string | null;
    /** Режим, назначенный сервером: `read` — только чтение, `write` — полный доступ. */
    mode?: 'read' | 'write' | null;
  } | null;
  onExitImpersonation?: () => void;
  accessState?: AccessStateInfo | null;
  shouldRemind2FA?: boolean;
  onSetup2FA?: () => void;
  children?: ReactNode;
}

/**
 * StackedBanners — стопка info/warning/error баннеров под TopBar,
 * до Content. Mirrors frontend/src/components/AppShell.tsx порядок:
 *   1. SeasonEditUnlockBanner (yellow)
 *   2. impersonation banner (red)
 *   3. AccessStateBanner (depends)
 *   4. 2FA reminder (yellow)
 *   5. SetupIncompleteBanner (amber, AK-584) — пока в тенанте нет полей
 *   6. children (e.g. OverdueSowingsBanner на странице сама делает)
 */
export function StackedBanners({
  impersonation,
  onExitImpersonation,
  accessState,
  shouldRemind2FA,
  onSetup2FA,
  children,
}: StackedBannersProps) {
  const [dismissed2FAUntil, setDismissed2FAUntil] = useState<number>(() =>
    read2FADismissedUntil(),
  );
  const is2FADismissed = Date.now() < dismissed2FAUntil;

  const handleDismiss2FA = () => {
    const until = Date.now() + TWO_FA_DISMISS_TTL_MS;
    try {
      localStorage.setItem(TWO_FA_DISMISS_KEY, String(until));
    } catch {
      // ignore quota / private mode
    }
    setDismissed2FAUntil(until);
  };

  return (
    <>
      <SeasonEditUnlockBanner />

      {impersonation?.is_impersonating && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground">
          <span className="flex min-w-0 items-center gap-2">
            <Shield className="h-4 w-4 shrink-0" />
            {/* Имя и режим — язык пользователя, не модели: «только чтение» /
                «полный доступ», без слов «read», «write», «impersonation». */}
            {impersonation.user_full_name ? (
              <span className="min-w-0">
                Вы работаете под:{' '}
                <strong>{impersonation.user_full_name}</strong>
                {impersonation.mode
                  ? ` — ${
                      impersonation.mode === 'read'
                        ? 'только чтение'
                        : 'полный доступ'
                    }`
                  : ''}
              </span>
            ) : (
              <span className="min-w-0">
                Режим тех.поддержки: <strong>{impersonation.tenant_name}</strong>{' '}
                · действия логируются
              </span>
            )}
          </span>
          {onExitImpersonation && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onExitImpersonation}
              className="shrink-0 text-destructive"
            >
              {impersonation.user_full_name
                ? 'Выйти'
                : 'Выйти из режима поддержки'}
            </Button>
          )}
        </div>
      )}

      {accessState && accessState.state !== 'active' && (
        <AccessStateBanner info={accessState} />
      )}

      {shouldRemind2FA && !is2FADismissed && (
        <Alert
          variant="warning"
          // Compact: py-1.5 px-4 ≈ 30% ниже стандартного p-4 (founder 2026-05-02).
          className="rounded-none border-x-0 border-t-0 flex items-center justify-between gap-3 py-1.5 px-4"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <AlertDescription className="text-sm leading-tight">
              Ваша роль требует двухфакторную аутентификацию. Рекомендуем
              настроить её сейчас.
            </AlertDescription>
          </div>
          <div className="flex items-center gap-1">
            {onSetup2FA && (
              /* Внутри цветного Alert первичная (зелёная) кнопка спорит с
                 тоном баннера — два «главных» цвета в 300px. Внутри алертов
                 действие — `outline` (design review 2026-08-18, зона 2 · А10). */
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={onSetup2FA}
              >
                Настроить 2FA
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              aria-label="Скрыть напоминание о 2FA на 24 часа"
              onClick={handleDismiss2FA}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      <SetupIncompleteBanner />

      {children}
    </>
  );
}

export default StackedBanners;
